/**
 * Story 546, design byok.notice.web: the AI Services section of the project
 * settings page (layout approved by the operator, 2026-10-12).
 *
 * No save button. Choosing Ceetrix applies at once and deletes the saved
 * service; choosing "I choose the service" shows the fields and saves nothing
 * until an entry passes its check. Each field saves when it is left or Enter is
 * pressed; the server checks the change before saving anything, and a change
 * that fails leaves the saved service in force. One button, Test, carries the
 * last status: a tick, or a cross with the reason in a bar beneath it.
 *
 * The key is never shown back: the field is empty and its placeholder names
 * the last four characters of the saved key.
 */

import { useCallback, useState, type KeyboardEvent } from 'react';
import {
  useSaveAiService,
  useRemoveAiService,
  useTestAiService,
  type AiServiceEffort,
  type AiServiceResult,
  type AiServiceSummary,
} from '../../hooks/useProjectSettings';
import type { SettingsSectionProps } from './settingsSections';
import {
  AI_SERVICE_CHOICE_LEGEND,
  AI_SERVICE_CHOICE_CEETRIX,
  AI_SERVICE_CHOICE_OWN,
  AI_SERVICE_CEETRIX_NOTE,
  AI_SERVICE_NOT_YET_NOTE,
  AI_SERVICE_ADDRESS_LABEL,
  AI_SERVICE_ADDRESS_DEFAULT,
  AI_SERVICE_ADDRESS_HINT,
  AI_SERVICE_KEY_LABEL,
  AI_SERVICE_KEY_SAVED_PLACEHOLDER,
  AI_SERVICE_KEY_GUIDANCE,
  AI_SERVICE_MODEL_LABEL,
  AI_SERVICE_EFFORT_LABEL,
  AI_SERVICE_EFFORT_DEFAULT,
  AI_SERVICE_EFFORT_LABELS,
  AI_SERVICE_FORMAT_LABELS,
  AI_SERVICE_TEST_LABEL,
  AI_SERVICE_CHECKING_LABEL,
  AI_SERVICE_WORKS,
  AI_SERVICE_PAUSED,
  AI_SERVICE_CHECK_SUBJECT,
  AI_SERVICE_LAST_CHECK_FAILURE,
  AI_SERVICE_STATE_MESSAGES,
  AI_SERVICE_REQUEST_FAILED,
} from '../workspace/projectSettingsCopy';

const EFFORTS: readonly AiServiceEffort[] = ['low', 'medium', 'high'];
const HTTP_FORBIDDEN = 403;

type Status =
  | { kind: 'none' }
  | { kind: 'checking' }
  | { kind: 'ok' }
  | { kind: 'error'; message: string };

/** The status a saved service shows before anything is pressed. */
function statusOf(summary: AiServiceSummary | null | undefined): Status {
  if (!summary) return { kind: 'none' };
  if (summary.state === 'working') return { kind: 'ok' };
  if (summary.state === 'customer_fix' || summary.state === 'stopped' || summary.state === 'unusable') {
    const what = AI_SERVICE_STATE_MESSAGES[summary.state];
    return { kind: 'error', message: summary.reason ? `${what} The provider said: ${summary.reason}` : what };
  }
  return { kind: 'none' };
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString();
}

/** A day and a time, such as "16 Oct, 14:05". */
function dayAndTimeOf(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function AiServicesSection({ projectSlug, settings, readOnly, onRefusedForRole }: SettingsSectionProps) {
  const saved = settings.ai_service ?? null;
  const save = useSaveAiService(projectSlug);
  const remove = useRemoveAiService(projectSlug);
  const test = useTestAiService(projectSlug);

  const [choice, setChoice] = useState<'ceetrix' | 'own'>(saved ? 'own' : 'ceetrix');
  const [address, setAddress] = useState(saved?.address ?? AI_SERVICE_ADDRESS_DEFAULT);
  const [model, setModel] = useState(saved?.model ?? '');
  const [effort, setEffort] = useState<AiServiceEffort | null>(saved?.effort ?? null);
  const [key, setKey] = useState('');
  const [status, setStatus] = useState<Status>(statusOf(saved));

  const busy = save.isPending || remove.isPending || test.isPending;

  const show = useCallback((result: AiServiceResult) => {
    if (result.ok) {
      setStatus({ kind: 'ok' });
      return;
    }
    setStatus({ kind: 'error', message: result.message ?? AI_SERVICE_REQUEST_FAILED });
  }, []);

  const failed = useCallback((err: unknown) => {
    if ((err as { status?: number })?.status === HTTP_FORBIDDEN) {
      onRefusedForRole();
      return;
    }
    setStatus({ kind: 'error', message: AI_SERVICE_REQUEST_FAILED });
  }, [onRefusedForRole]);

  /** Autosave: the full candidate; skipped while incomplete or unchanged. */
  const commit = useCallback(async (next: { address?: string; model?: string; effort?: AiServiceEffort | null; key?: string } = {}) => {
    if (readOnly || busy) return;
    const candidate = {
      address: (next.address ?? address).trim(),
      model: (next.model ?? model).trim(),
      effort: next.effort === undefined ? effort : next.effort,
      key: (next.key ?? key).trim(),
    };
    if (!candidate.address || !candidate.model || (!candidate.key && !saved)) return;
    const unchanged = saved
      && !candidate.key
      && candidate.address === saved.address
      && candidate.model === saved.model
      && candidate.effort === saved.effort;
    if (unchanged) return;
    setStatus({ kind: 'checking' });
    try {
      const result = await save.mutateAsync({
        address: candidate.address,
        model: candidate.model,
        effort: candidate.effort,
        ...(candidate.key ? { key: candidate.key } : {}),
      });
      show(result);
      if (result.ok) {
        setKey('');
        if (result.ai_service) setAddress(result.ai_service.address);
      }
    } catch (err) {
      failed(err);
    }
  }, [readOnly, busy, address, model, effort, key, saved, save, show, failed]);

  const onEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commit();
    }
  };

  const chooseCeetrix = useCallback(async () => {
    if (readOnly || busy || choice === 'ceetrix') return;
    setChoice('ceetrix');
    setStatus({ kind: 'none' });
    if (!saved) return;
    try {
      await remove.mutateAsync(undefined);
      setAddress(AI_SERVICE_ADDRESS_DEFAULT);
      setModel('');
      setEffort(null);
      setKey('');
    } catch (err) {
      setChoice('own');
      failed(err);
    }
  }, [readOnly, busy, choice, saved, remove, failed]);

  const runTest = useCallback(async () => {
    if (busy || !saved) return;
    setStatus({ kind: 'checking' });
    try {
      show(await test.mutateAsync(undefined));
    } catch (err) {
      failed(err);
    }
  }, [busy, saved, test, show, failed]);

  const supported = (level: AiServiceEffort) =>
    saved?.format !== 'anthropic' || saved.effort_levels === null || saved.effort_levels.includes(level);

  return (
    <>
      <div className="project-settings__option" role="radiogroup" aria-label={AI_SERVICE_CHOICE_LEGEND}>
        <span className="project-settings__choice">{AI_SERVICE_CHOICE_LEGEND}</span>
        <label className="ai-services__radio">
          <input type="radio" name="ai-service-choice" checked={choice === 'ceetrix'} disabled={readOnly || busy} onChange={() => void chooseCeetrix()} />
          {AI_SERVICE_CHOICE_CEETRIX}
        </label>
        <label className="ai-services__radio">
          <input type="radio" name="ai-service-choice" checked={choice === 'own'} disabled={readOnly || busy} onChange={() => setChoice('own')} />
          {AI_SERVICE_CHOICE_OWN}
        </label>
        {choice === 'ceetrix' && (
          <ul className="project-settings__guidance"><li>{AI_SERVICE_CEETRIX_NOTE}</li></ul>
        )}
      </div>

      {choice === 'own' && (
        <div className="ai-services__fields">
          {!saved && <p className="project-settings__note">{AI_SERVICE_NOT_YET_NOTE}</p>}

          <label className="ai-services__field">
            <span className="ai-services__label">{AI_SERVICE_ADDRESS_LABEL}</span>
            <input
              type="url"
              value={address}
              disabled={readOnly}
              onChange={(e) => setAddress(e.target.value)}
              onBlur={() => void commit()}
              onKeyDown={onEnter}
            />
          </label>
          <p className="ai-services__hint">
            {saved ? `${AI_SERVICE_FORMAT_LABELS[saved.format]}. ` : ''}{AI_SERVICE_ADDRESS_HINT}
          </p>

          <label className="ai-services__field">
            <span className="ai-services__label">{AI_SERVICE_KEY_LABEL}</span>
            <input
              type="password"
              autoComplete="off"
              value={key}
              placeholder={saved ? AI_SERVICE_KEY_SAVED_PLACEHOLDER(saved.key_last4) : ''}
              disabled={readOnly}
              onChange={(e) => setKey(e.target.value)}
              onBlur={() => void commit()}
              onKeyDown={onEnter}
            />
          </label>
          <ul className="project-settings__guidance">
            {AI_SERVICE_KEY_GUIDANCE.map((line) => <li key={line}>{line}</li>)}
          </ul>

          <label className="ai-services__field">
            <span className="ai-services__label">{AI_SERVICE_MODEL_LABEL}</span>
            <input
              type="text"
              value={model}
              disabled={readOnly}
              onChange={(e) => setModel(e.target.value)}
              onBlur={() => void commit()}
              onKeyDown={onEnter}
            />
          </label>

          <div className="ai-services__field" role="group" aria-label={AI_SERVICE_EFFORT_LABEL}>
            <span className="ai-services__label">{AI_SERVICE_EFFORT_LABEL}</span>
            <div className="ai-services__segments">
              <button
                type="button"
                aria-pressed={effort === null}
                disabled={readOnly || busy}
                onClick={() => { setEffort(null); void commit({ effort: null }); }}
              >
                {AI_SERVICE_EFFORT_DEFAULT}
              </button>
              {EFFORTS.map((level) => (
                <button
                  key={level}
                  type="button"
                  aria-pressed={effort === level}
                  disabled={readOnly || busy || !supported(level)}
                  onClick={() => { setEffort(level); void commit({ effort: level }); }}
                >
                  {AI_SERVICE_EFFORT_LABELS[level]}
                </button>
              ))}
            </div>
          </div>

          {saved?.state === 'paused' && saved.next_try_at && (
            <p className="ai-services__paused" role="status">{AI_SERVICE_PAUSED(saved.reason ?? 'the provider asked Ceetrix to wait', timeOf(saved.next_try_at))}</p>
          )}

          {saved?.last_check_failure && (
            <p className="ai-services__last-check-failure" data-testid="ai-services-last-check-failure">
              {AI_SERVICE_LAST_CHECK_FAILURE(
                AI_SERVICE_CHECK_SUBJECT(saved.last_check_failure.check, saved.last_check_failure.ref),
                dayAndTimeOf(saved.last_check_failure.at),
                saved.last_check_failure.words,
              )}
            </p>
          )}

          <div className="ai-services__actions">
            <button
              type="button"
              className="ai-services__test"
              data-status={status.kind}
              disabled={busy || !saved}
              onClick={() => void runTest()}
            >
              {status.kind === 'ok' && <span className="ai-services__tick" aria-label={AI_SERVICE_WORKS}>✓</span>}
              {status.kind === 'error' && <span className="ai-services__cross" aria-hidden="true">✕</span>}
              {status.kind === 'checking' ? AI_SERVICE_CHECKING_LABEL : AI_SERVICE_TEST_LABEL}
            </button>
          </div>
          {status.kind === 'error' && (
            <p className="ai-services__error-bar" role="alert">{status.message}</p>
          )}
        </div>
      )}
    </>
  );
}
